import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import moment from "moment";
import { PageHeader } from "components/ui";
import { useFetch } from "hooks";
import { get, post, del } from "utils/api";
import { errorMessage, message } from "actions/message";
import "./style.scss";

// Plain (non-redux) API calls for this feature — the client prepends the API host
// and attaches auth headers. Paths omit the "api/" prefix.
const api = {
  list: () => get("system-users"),
  resolve: email => get("system-users/resolve", { email }),
  add: (category, email) => post(`system-users/${category}/members`, { email }),
  remove: id => del(`system-users/members/${id}`)
};

// Friendly section titles for the raw category keys returned by the API.
const CATEGORY_LABELS = {
  SuperAdmin: "Super Admins",
  NewCompanyAccess: "New Company Access",
  Synthpop: "Synthpop Requestor",
  ComplianceExpire: "Compliance Expiration Requestor",
  Default: "Default Requestor",
  // S3DEV2-7540 identity keys (migrated from hardcoded emails).
  IVR: "IVR Requestor",
  Automation: "Automation Requestor",
  SystemImporter: "System Importer",
  APIIntegration: "API Integration Requestor",
  MedSouth: "MedSouth Requestor",
  PhoneContactHolder: "Phone Contact Holder",
  AutoFitProcessor: "Auto-Fit Processor",
  AutoShip: "Auto-Ship Requestor"
};

const labelFor = category =>
  CATEGORY_LABELS[category] || category.replace(/([a-z])([A-Z])/g, "$1 $2");

// Frontend overrides for the per-category description. When present these take
// precedence over the backend-supplied description; anything not listed falls
// back to view.description.
const CATEGORY_DESCRIPTIONS = {
  SuperAdmin:
    "Super Admins can edit times on the Team Dashboard — that is the only capability this grants today."
};

const descriptionFor = view =>
  CATEGORY_DESCRIPTIONS[view.category] || view.description;

const formatDate = value => (value ? moment(value).format("MM/DD/YYYY") : "—");

// Enter/Space activation so the link-styled span controls below stay usable by
// keyboard and assistive tech while keeping the existing inline layout.
const onActivate = handler => e => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    handler();
  }
};

// The backend returns 400 with a plain string body (e.Message). Pull that out so
// the user sees the real reason (duplicate, unknown email, last super admin, etc.)
// instead of a generic failure.
const serverMessage = (error, fallback) => {
  const body = error && error.response ? error.response.body : undefined;
  if (typeof body === "string" && body.trim()) return body;
  const text = error && error.response ? error.response.text : undefined;
  if (typeof text === "string" && text.trim()) {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === "string" && parsed.trim()) return parsed;
      if (parsed && parsed.Message) return parsed.Message;
    } catch (e) {
      return text;
    }
  }
  return fallback;
};

const MemberRow = ({ member, onRemove }) => (
  <tr>
    <td>{member.name || "—"}</td>
    <td>{member.email || "—"}</td>
    <td>{member.active ? "Yes" : "No"}</td>
    <td>{member.addedByName || "—"}</td>
    <td>{formatDate(member.dtCreated)}</td>
    <td>
      <span
        className="su-remove"
        role="button"
        tabIndex={0}
        onClick={() => onRemove(member)}
        onKeyDown={onActivate(() => onRemove(member))}
      >
        Remove
      </span>
    </td>
  </tr>
);

MemberRow.propTypes = {
  member: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired
};

const CategorySection = ({
  view,
  inputValue,
  pending,
  editing,
  busy,
  onInputChange,
  onSubmit,
  onConfirm,
  onCancel,
  onEdit,
  onCancelEdit,
  onRemove
}) => {
  const isRequestor = view.kind === "requestor";
  const members = view.members || [];
  const hasCurrent = members.length > 0;
  const actionLabel = isRequestor ? "Set" : "Add";

  // For single-login requestor categories the form is hidden behind an explicit
  // "Change" affordance once a login is set, so the current value isn't accidentally
  // overwritten. List categories (and unset requestors) always show the add form.
  const showForm = isRequestor ? editing || !hasCurrent : true;

  return (
    <section className="su-category">
      <h3>{labelFor(view.category)}</h3>
      <p className="su-description">{descriptionFor(view)}</p>

      {isRequestor ? (
        <div className="su-requestor-current">
          <strong>Current: </strong>
          {hasCurrent ? (
            <span>
              {members[0].name || "—"} ({members[0].email || "—"})
              {!members[0].active && <em> — inactive</em>}
              {!showForm && (
                <span
                  className="su-change"
                  role="button"
                  tabIndex={0}
                  onClick={() => onEdit(view.category, members[0].email)}
                  onKeyDown={onActivate(() =>
                    onEdit(view.category, members[0].email)
                  )}
                >
                  Change
                </span>
              )}
              <span
                className="su-remove"
                role="button"
                tabIndex={0}
                onClick={() => onRemove(members[0])}
                onKeyDown={onActivate(() => onRemove(members[0]))}
              >
                Clear
              </span>
            </span>
          ) : (
            <span className="su-none">None set</span>
          )}
        </div>
      ) : (
        <table className="su-members">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Active</th>
              <th>Added by</th>
              <th>Added on</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.length ? (
              members.map(m => (
                <MemberRow key={m.id} member={m} onRemove={onRemove} />
              ))
            ) : (
              <tr>
                <td colSpan={6} className="su-none">
                  No members yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {pending ? (
        <div className="su-confirm">
          <span>
            {actionLabel} <strong>{pending.name}</strong> ({pending.email})?
          </span>
          <button
            type="button"
            className="su-btn su-btn-primary"
            disabled={busy}
            onClick={() => onConfirm(view.category)}
          >
            Confirm
          </button>
          <button
            type="button"
            className="su-btn"
            disabled={busy}
            onClick={() => onCancel(view.category)}
          >
            Cancel
          </button>
        </div>
      ) : showForm ? (
        <form
          className="su-add"
          onSubmit={e => {
            e.preventDefault();
            onSubmit(view.category);
          }}
        >
          <input
            type="email"
            placeholder="user@example.com"
            value={inputValue || ""}
            disabled={busy}
            onChange={e => onInputChange(view.category, e.target.value)}
          />
          <button
            type="submit"
            className="su-btn su-btn-primary"
            disabled={busy || !inputValue}
          >
            {actionLabel}
          </button>
          {/* Only single-login requestors that already have a value can cancel back
              to the read-only "Current" display. */}
          {isRequestor && hasCurrent && (
            <button
              type="button"
              className="su-btn"
              disabled={busy}
              onClick={() => onCancelEdit(view.category)}
            >
              Cancel
            </button>
          )}
        </form>
      ) : null}
    </section>
  );
};

CategorySection.propTypes = {
  view: PropTypes.object.isRequired,
  inputValue: PropTypes.string,
  pending: PropTypes.object,
  editing: PropTypes.bool,
  busy: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

const SystemUsersAdmin = () => {
  const dispatch = useDispatch();
  const [inputs, setInputs] = React.useState({});
  const [pending, setPending] = React.useState({});
  const [editing, setEditing] = React.useState({});
  const [busy, setBusy] = React.useState({});

  const {
    response: views,
    fetchData: loadSystemUsers,
    isFetching,
    hasFetched
  } = useFetch({
    apiFn: api.list,
    defaultValue: [],
    clearDataOnFetch: false, // keep the current lists visible while refetching
    onError: error =>
      dispatch(errorMessage(serverMessage(error, "Failed to load system users.")))
  });

  React.useEffect(() => {
    loadSystemUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBusyFor = (category, value) =>
    setBusy(prev => ({ ...prev, [category]: value }));

  const onInputChange = (category, value) =>
    setInputs(prev => ({ ...prev, [category]: value }));

  // Reveal the change form for a single-login requestor, pre-filled with the current email.
  const onEdit = (category, currentEmail) => {
    setInputs(prev => ({ ...prev, [category]: currentEmail || "" }));
    setEditing(prev => ({ ...prev, [category]: true }));
  };

  // Collapse the change form back to the read-only "Current" display.
  const onCancelEdit = category => {
    setEditing(prev => ({ ...prev, [category]: false }));
    setInputs(prev => ({ ...prev, [category]: "" }));
    setPending(prev => ({ ...prev, [category]: null }));
  };

  // Resolve the email first so the admin confirms the actual person before adding.
  const onSubmit = async category => {
    const email = (inputs[category] || "").trim();
    if (!email) return;
    try {
      setBusyFor(category, true);
      const resolved = await api.resolve(email);
      setPending(prev => ({ ...prev, [category]: resolved }));
    } catch (error) {
      dispatch(
        errorMessage(serverMessage(error, "No active user found for that email."))
      );
    } finally {
      setBusyFor(category, false);
    }
  };

  const onConfirm = async category => {
    const resolved = pending[category];
    if (!resolved) return;
    try {
      setBusyFor(category, true);
      await api.add(category, resolved.email);
      setPending(prev => ({ ...prev, [category]: null }));
      setInputs(prev => ({ ...prev, [category]: "" }));
      setEditing(prev => ({ ...prev, [category]: false }));
      dispatch(message("User added."));
      await loadSystemUsers();
    } catch (error) {
      dispatch(errorMessage(serverMessage(error, "Failed to add user.")));
    } finally {
      setBusyFor(category, false);
    }
  };

  // Cancelling the resolve confirmation collapses a single-login requestor back to
  // its read-only view (parallels onCancelEdit); harmless for list categories.
  const onCancel = category => {
    setPending(prev => ({ ...prev, [category]: null }));
    setEditing(prev => ({ ...prev, [category]: false }));
  };

  const onRemove = async member => {
    if (
      !window.confirm(
        `Remove ${member.name || member.email} from this category?`
      )
    )
      return;
    try {
      setBusyFor(member.category, true);
      await api.remove(member.id);
      // Clear any in-progress edit/confirm so the form doesn't reappear pre-filled
      // with the user that was just removed.
      setEditing(prev => ({ ...prev, [member.category]: false }));
      setInputs(prev => ({ ...prev, [member.category]: "" }));
      setPending(prev => ({ ...prev, [member.category]: null }));
      dispatch(message("User removed."));
      await loadSystemUsers();
    } catch (error) {
      dispatch(errorMessage(serverMessage(error, "Failed to remove user.")));
    } finally {
      setBusyFor(member.category, false);
    }
  };

  const categoryViews = views || [];

  return (
    <div className="system-users-admin">
      <PageHeader title="System Users" />
      {isFetching && !hasFetched ? (
        <div className="su-loading">Loading…</div>
      ) : (
        categoryViews.map(view => (
          <CategorySection
            key={view.category}
            view={view}
            inputValue={inputs[view.category]}
            pending={pending[view.category]}
            editing={!!editing[view.category]}
            busy={!!busy[view.category]}
            onInputChange={onInputChange}
            onSubmit={onSubmit}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onEdit={onEdit}
            onCancelEdit={onCancelEdit}
            onRemove={m => onRemove({ ...m, category: view.category })}
          />
        ))
      )}
    </div>
  );
};

export default SystemUsersAdmin;
